import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Chip,
  Divider,
  RadioGroup,
  Radio,
  LinearProgress,
  Collapse
} from '@mui/material';
import {
  AutoFixHigh as TailorIcon,
  Language as LanguageIcon,
  School as ReadingIcon,
  Public as CultureIcon
} from '@mui/icons-material';

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  grade_level?: string;
  reading_level?: number;
  primary_language?: string;
  cultures: string[];
}

interface Document {
  id: number;
  name: string;
  description?: string;
  grade_level?: string;
  reading_level?: number;
}

interface Culture {
  id: number;
  code: string;
  name: string;
  description?: string;
}

interface TailorAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document | null;
  student: Student | null;
  onSuccess?: () => void;
}

const TailorAssignmentDialog: React.FC<TailorAssignmentDialogProps> = ({
  open,
  onClose,
  document,
  student,
  onSuccess
}) => {
  const { t } = useTranslation();
  const { token, isAuthenticated } = useAuth();
  
  // Form state
  const [cultures, setCultures] = useState<Culture[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Progress tracking state
  const [progressStage, setProgressStage] = useState<string>('');
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [elementsCount, setElementsCount] = useState<number>(0);
  const [progressHistory, setProgressHistory] = useState<string[]>([]);
  
  // Output format: 'pdf' (classic) or 'interactive' (fillable HTML)
  const [outputFormat, setOutputFormat] = useState<'pdf' | 'interactive'>('pdf');
  
  // Tailoring options
  const [enableCulturalAdaptation, setEnableCulturalAdaptation] = useState(false);
  const [enableReadingLevelAdjustment, setEnableReadingLevelAdjustment] = useState(false);
  const [enableLanguageChange, setEnableLanguageChange] = useState(false);
  
  // Specific settings
  const [selectedCultures, setSelectedCultures] = useState<number[]>([]);
  const [adjustmentType, setAdjustmentType] = useState<'reading_level' | 'grade_level'>('reading_level');
  const [targetReadingLevel, setTargetReadingLevel] = useState<number>(0);
  const [targetGradeLevel, setTargetGradeLevel] = useState<string>('');
  const [targetLanguage, setTargetLanguage] = useState<string>('en');
  const [tailoringNotes, setTailoringNotes] = useState('');

  // Fetch cultures when dialog opens
  useEffect(() => {
    if (open && token) {
      fetchCultures();
    }
  }, [open, token]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open && document && student) {
      // Pre-populate with student's existing data
      setEnableCulturalAdaptation(student.cultures && student.cultures.length > 0);
      setEnableReadingLevelAdjustment(true); // Enable by default for easy tailoring
      
      // Check if student has a non-English primary language
      // If so, enable language translation by default and set to student's language
      const studentLang = student.primary_language;
      const hasNonEnglishLanguage = !!(studentLang && studentLang !== 'en' && studentLang !== '');
      setEnableLanguageChange(hasNonEnglishLanguage);
      setTargetLanguage(hasNonEnglishLanguage ? studentLang : 'en');
      
      // Default to Grade Level adjustment (most common use case)
      // Pre-populate with student's grade level for minimal clicks
      setAdjustmentType('grade_level');
      setTargetGradeLevel(student.grade_level || '');
      setTargetReadingLevel(student.reading_level || 0);
      
      setTailoringNotes('');
      setError(null);
    }
  }, [open, document, student]);

  const fetchCultures = async () => {
    try {
      const response = await fetch('/api/students/cultures', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('tailoring.errors.fetchCulturesWithStatus', { status: response.status }));
      }

      const data = await response.json();
      setCultures(data.cultures);
      
      // Pre-populate selected cultures based on student's existing cultural backgrounds
      if (student && student.cultures && student.cultures.length > 0) {
        const studentCultureIds = data.cultures
          .filter((culture: Culture) => student.cultures.includes(culture.name))
          .map((culture: Culture) => culture.id);
        setSelectedCultures(studentCultureIds);
      }
    } catch (err) {
      console.error('Error fetching cultures:', err);
      setError(t('tailoring.errors.loadCulturesFailed'));
    }
  };

  const handleCultureToggle = (cultureId: number) => {
    setSelectedCultures(prev => 
      prev.includes(cultureId) 
        ? prev.filter(id => id !== cultureId)
        : [...prev, cultureId]
    );
  };

  const handleTailor = async () => {
    if (!document || !student || !token || !isAuthenticated) return;

    // Validate that at least one tailoring option is enabled
    if (!enableCulturalAdaptation && !enableReadingLevelAdjustment && !enableLanguageChange) {
      setError(t('tailoring.errors.enableAtLeastOne'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setProgressStage('');
      setProgressMessage(t('tailoring.starting'));
      setCurrentPage(0);
      setTotalPages(0);
      setElementsCount(0);
      setProgressHistory([]);

      const tailoringData = {
        student_id: student.id,
        cultural_adaptation: enableCulturalAdaptation,
        reading_level_adjustment: enableReadingLevelAdjustment,
        language_change: enableLanguageChange,
        selected_cultures: selectedCultures,
        adjustment_type: adjustmentType,
        target_reading_level: adjustmentType === 'reading_level' ? targetReadingLevel : null,
        target_grade_level: adjustmentType === 'grade_level' ? targetGradeLevel : null,
        target_language: targetLanguage,
        tailoring_notes: tailoringNotes
      };

      // Choose endpoint based on output format
      const endpoint = outputFormat === 'interactive'
        ? `/api/documents/${document.id}/tailor-interactive?token=${encodeURIComponent(token)}`
        : `/api/documents/${document.id}/tailor-stream?token=${encodeURIComponent(token)}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tailoringData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('tailoring.errors.startFailed'));
      }

      // Read SSE stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error(t('tailoring.errors.noStream'));
      }

      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            let parsed;
            try {
              parsed = JSON.parse(line.slice(6));
            } catch {
              if (line.slice(6).trim()) {
                console.warn('Failed to parse SSE data:', line);
              }
              continue;
            }

            if (parsed.type === 'progress') {
              setProgressStage(parsed.stage);
              setProgressMessage(parsed.message);
              setCurrentPage(parsed.current_page);
              setTotalPages(parsed.total_pages);
              setElementsCount(parsed.elements_count);
              
              if (parsed.stage === 'page_complete' || parsed.stage === 'detected') {
                setProgressHistory(prev => [...prev.slice(-5), parsed.message]);
              }
            } else if (parsed.type === 'complete') {
              const stats = parsed.result?.tailoring_stats;
              console.log(`Assignment tailored successfully! Transformed ${stats?.transformed_text_count || 0} text elements.`);
              handleClose();
              if (onSuccess) onSuccess();
              return;
            } else if (parsed.type === 'error') {
              throw new Error(parsed.message || t('tailoring.errors.tailoringFailedGeneric'));
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Error tailoring assignment:', error);
      setError(error instanceof Error ? error.message : t('tailoring.errors.tailorFailed'));
    } finally {
      setLoading(false);
      setProgressStage('');
      setProgressMessage('');
      setProgressHistory([]);
    }
  };

  const handleClose = () => {
    setEnableCulturalAdaptation(false);
    setEnableReadingLevelAdjustment(false);
    setEnableLanguageChange(false);
    setSelectedCultures([]);
    setAdjustmentType('reading_level');
    setTargetReadingLevel(0);
    setTargetGradeLevel('');
    setTargetLanguage('en');
    setTailoringNotes('');
    setError(null);
    // Reset progress state
    setProgressStage('');
    setProgressMessage('');
    setCurrentPage(0);
    setTotalPages(0);
    setElementsCount(0);
    setProgressHistory([]);
    onClose();
  };

  if (!document || !student) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TailorIcon />
          Tailor Assignment for {student.full_name}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Customize this assignment based on {student.full_name}'s learning profile
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Student Information Card */}
        <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Student Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography><strong>Name:</strong> {student.full_name}</Typography>
              {student.grade_level && (
                <Typography><strong>Grade Level:</strong> {student.grade_level}</Typography>
              )}
              {student.reading_level && (
                <Typography><strong>{t('tailoring.currentReadingLevel')}</strong> {student.reading_level}</Typography>
              )}
              {student.primary_language && (
                <Typography><strong>{t('students.form.primaryLanguage')}:</strong> {t(`students.form.languages.${student.primary_language}`)}</Typography>
              )}
              {student.cultures.length > 0 && (
                <Typography><strong>{t('students.form.raceEthnicity')}:</strong> {student.cultures.join(', ')}</Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Original Document Information */}
        <Card sx={{ mb: 3, bgcolor: '#f0f7ff' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Original Document
            </Typography>
            <Typography><strong>Name:</strong> {document.name}</Typography>
            {document.description && (
              <Typography><strong>{t('tailoring.documentDescriptionLabel')}</strong> {document.description}</Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              {t('tailoring.notePdfDocx')}
            </Typography>
          </CardContent>
        </Card>

        {/* Output Format Selector */}
        <Card sx={{ mb: 3, bgcolor: '#f5f0ff' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('tailoring.outputFormat')}
            </Typography>
            <RadioGroup
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value as 'pdf' | 'interactive')}
              aria-label={t('tailoring.outputFormat')}
            >
              <FormControlLabel
                value="pdf"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {t('tailoring.outputPdf')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('tailoring.outputPdfDescription')}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="interactive"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {t('tailoring.outputInteractive')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('tailoring.outputInteractiveDescription')}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Tailoring Options */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h6">
            {t('tailoring.optionsTitle')}
          </Typography>

          {/* 1. Reading Level / Grade Level Adjustment - First priority */}
          <Card sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReadingIcon color="primary" />
                <Typography variant="h6">{t('tailoring.levelAdjustment')}</Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableReadingLevelAdjustment}
                    onChange={(e) => setEnableReadingLevelAdjustment(e.target.checked)}
                    aria-label={t('tailoring.enableLevelAdjustment')}
                  />
                }
                label={t('common.enable')}
              />
            </Box>
            {enableReadingLevelAdjustment && (
              <Box sx={{ mt: 2 }}>
                {/* Adjustment Type Toggle */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('tailoring.adjustByDescription')}
                </Typography>
                <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                  <RadioGroup
                    value={adjustmentType}
                    onChange={(e) => setAdjustmentType(e.target.value as 'reading_level' | 'grade_level')}
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'row', 
                      gap: 3,
                      mb: 2
                    }}
                  >
                    <FormControlLabel 
                      value="grade_level" 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {t('tailoring.byGradeLevel')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('tailoring.gradeLevelDesc')}
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel 
                      value="reading_level" 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {t('tailoring.byReadingLevel')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('tailoring.lexileScale')}
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                {/* Input based on adjustment type */}
                {adjustmentType === 'reading_level' ? (
                  <TextField
                    label={t('tailoring.targetReadingLevel')}
                    type="number"
                    value={targetReadingLevel}
                    onChange={(e) => setTargetReadingLevel(parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0 }}
                    helperText={t('tailoring.documentReadingLevelHelper', { docLevel: document.reading_level || t('common.notSet') })}
                    sx={{ width: 250 }}
                    aria-label={t('tailoring.targetReadingLevel')}
                  />
                ) : (
                  <FormControl sx={{ minWidth: 250 }}>
                    <InputLabel id="target-grade-level-label">{t('tailoring.targetGradeLevel')}</InputLabel>
                    <Select
                      labelId="target-grade-level-label"
                      value={targetGradeLevel}
                      onChange={(e) => setTargetGradeLevel(e.target.value)}
                      label={t('tailoring.targetGradeLevel')}
                      aria-label={t('tailoring.targetGradeLevel')}
                    >
                      <MenuItem value="K">{t('students.form.grades.K')}</MenuItem>
                      <MenuItem value="1st">{t('students.form.grades.1st')}</MenuItem>
                      <MenuItem value="2nd">{t('students.form.grades.2nd')}</MenuItem>
                      <MenuItem value="3rd">{t('students.form.grades.3rd')}</MenuItem>
                      <MenuItem value="4th">{t('students.form.grades.4th')}</MenuItem>
                      <MenuItem value="5th">{t('students.form.grades.5th')}</MenuItem>
                      <MenuItem value="6th">{t('students.form.grades.6th')}</MenuItem>
                      <MenuItem value="7th">{t('students.form.grades.7th')}</MenuItem>
                      <MenuItem value="8th">{t('students.form.grades.8th')}</MenuItem>
                      <MenuItem value="9th">{t('students.form.grades.9th')}</MenuItem>
                      <MenuItem value="10th">{t('students.form.grades.10th')}</MenuItem>
                      <MenuItem value="11th">{t('students.form.grades.11th')}</MenuItem>
                      <MenuItem value="12th">{t('students.form.grades.12th')}</MenuItem>
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t('tailoring.documentGradeLevelHelper', { 
                        docLevel: document.grade_level || t('common.notSet'),
                        studentLevel: student.grade_level || t('common.notSet')
                      })}
                    </Typography>
                  </FormControl>
                )}
              </Box>
            )}
          </Card>

          {/* 2. Language Change - Second priority */}
          <Card sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageIcon color="primary" />
                <Typography variant="h6">{t('tailoring.languageTranslation')}</Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableLanguageChange}
                    onChange={(e) => setEnableLanguageChange(e.target.checked)}
                  />
                }
                label={t('common.enable')}
              />
            </Box>
            {enableLanguageChange && (
              <Box sx={{ mt: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>{t('tailoring.targetLanguage')}</InputLabel>
                  <Select
                    value={targetLanguage}
                    onChange={(e) => setTargetLanguage(e.target.value)}
                    label={t('tailoring.targetLanguage')}
                    aria-label={t('tailoring.targetLanguage')}
                  >
                    <MenuItem value="en">{t('students.form.languages.en')}</MenuItem>
                    <MenuItem value="es">{t('students.form.languages.es')}</MenuItem>
                    <MenuItem value="fr">{t('students.form.languages.fr')}</MenuItem>
                    <MenuItem value="de">{t('students.form.languages.de')}</MenuItem>
                    <MenuItem value="zh">{t('students.form.languages.zh')}</MenuItem>
                    <MenuItem value="vi">{t('students.form.languages.vi')}</MenuItem>
                    <MenuItem value="ar">{t('students.form.languages.ar')}</MenuItem>
                    <MenuItem value="ko">{t('students.form.languages.ko')}</MenuItem>
                    <MenuItem value="tl">{t('students.form.languages.tl')}</MenuItem>
                    <MenuItem value="pt">{t('students.form.languages.pt')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </Card>

          {/* 3. Cultural Adaptation - Third priority (most sensitive) */}
          <Card sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CultureIcon color="primary" />
                <Typography variant="h6">Cultural Adaptation</Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableCulturalAdaptation}
                    onChange={(e) => setEnableCulturalAdaptation(e.target.checked)}
                  />
                }
                label="Enable"
              />
            </Box>
            {enableCulturalAdaptation && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select cultural backgrounds to adapt the content for:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {cultures.map((culture) => (
                    <Chip
                      key={culture.id}
                      label={culture.name}
                      onClick={() => handleCultureToggle(culture.id)}
                      color={selectedCultures.includes(culture.id) ? 'primary' : 'default'}
                      variant={selectedCultures.includes(culture.id) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Card>

          <Divider sx={{ my: 2 }} />

          {/* Tailoring Notes */}
          <TextField
            label={t('tailoring.additionalNotes')}
            value={tailoringNotes}
            onChange={(e) => setTailoringNotes(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Any specific instructions for tailoring this assignment..."
            helperText="Optional: Add any specific requirements or notes for the tailoring process"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 2, p: 2 }}>
        {/* Progress Display */}
        <Collapse in={loading}>
          <Box sx={{ 
            bgcolor: '#f5f5f5', 
            borderRadius: 2, 
            p: 2, 
            border: '1px solid #e0e0e0'
          }}>
            {/* Progress bar */}
            {totalPages > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('tailoring.processingPage', { current: currentPage, total: totalPages })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.round((currentPage / totalPages) * 100)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(currentPage / totalPages) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}
            
            {/* Current status message */}
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500, 
                color: '#1976d2',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box 
                component="span" 
                sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: '#1976d2',
                  animation: 'pulse 1.5s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.4 }
                  }
                }} 
              />
              {progressMessage || t('tailoring.startingProcess')}
            </Typography>
            
            {/* Progress history - show recent page completions */}
            {progressHistory.length > 0 && (
              <Box sx={{ mt: 1.5, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                {progressHistory.map((msg, idx) => (
                  <Typography 
                    key={idx} 
                    variant="caption" 
                    sx={{ 
                      display: 'block', 
                      color: '#666',
                      py: 0.25
                    }}
                  >
                    ✓ {msg}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
        
        {/* Action buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleClose} disabled={loading}>
            {t('modals.cancel')}
          </Button>
          <Button 
            onClick={handleTailor}
            variant="contained"
            disabled={(!enableCulturalAdaptation && !enableReadingLevelAdjustment && !enableLanguageChange) || loading}
            startIcon={<TailorIcon />}
          >
            {loading ? t('tailoring.processingWithAI') : t('tailoring.tailorAssignment')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TailorAssignmentDialog;
